import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Loader2, LogOut, Sparkles } from 'lucide-react';
import { ApiService } from '../../services/api';
import Turnstile from '../../components/Turnstile';
import WebStudentReportCard from '../practice-exams/components/WebStudentReportCard';
import { useAuth } from '../../contexts/AuthContext';
import type { ExternalResultSummary, KarneLookupResponse, PublicSchoolInfo } from '../../types/external-school';
import { KarneShell, RankCards, RankNote, buildReportData, formatDate, formatNet, rankText, saveBlob } from './shared';

/**
 * Kurum dışı öğrencinin karnesi: okulun bağlantısından T.C. kimlik + okul numarasıyla. Hesap yok; 30 dakikalık
 * karne oturumu yalnız bu sayfanın belleğinde tutulur.
 */
export default function PublicKarne() {
    const { slug = '' } = useParams();
    const { systemConfig } = useAuth();
    const [school, setSchool] = useState<PublicSchoolInfo | null>(null);
    const [linkError, setLinkError] = useState<string | null>(null);

    const [tc, setTc] = useState('');
    const [studentNumber, setStudentNumber] = useState('');
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const [turnstileKey, setTurnstileKey] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [session, setSession] = useState<KarneLookupResponse | null>(null);
    const [selected, setSelected] = useState<ExternalResultSummary | null>(null);
    const [reportData, setReportData] = useState<any>(null);
    const [loadingReport, setLoadingReport] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        ApiService.getKarneSchool(slug)
            .then((info) => { setSchool(info); document.title = `Karne · ${info.name}`; })
            .catch((err) => setLinkError(err?.message || 'Bağlantı geçersiz ya da süresi dolmuş'));
    }, [slug]);

    const logout = (message?: string) => {
        setSession(null);
        setSelected(null);
        setReportData(null);
        setTurnstileKey((k) => k + 1);
        if (message) setError(message);
    };

    const login = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!turnstileToken) return;
        setBusy(true);
        setError(null);
        try {
            const res = await ApiService.karneLookup(slug, { tc, studentNumber, turnstileToken });
            setSession(res);
            if (res.exams.length === 1) openExam(res.exams[0], res.token);
        } catch (err: any) {
            setError(err?.message || 'Bilgiler eşleşmedi');
            setTurnstileKey((k) => k + 1);
        } finally {
            setBusy(false);
        }
    };

    const openExam = async (exam: ExternalResultSummary, token = session?.token) => {
        if (!token) return;
        setSelected(exam);
        setReportData(null);
        setLoadingReport(true);
        try {
            setReportData(buildReportData(await ApiService.getKarneResult(slug, exam.resultId, token)));
        } catch (err: any) {
            if (err?.status === 401) logout('Oturum süresi doldu; lütfen yeniden giriş yapın.');
            else setError(err?.message || 'Karne açılamadı');
            setSelected(null);
        } finally {
            setLoadingReport(false);
        }
    };

    const downloadPdf = async () => {
        if (!session || !selected) return;
        setDownloading(true);
        try {
            saveBlob(await ApiService.downloadKarnePdf(slug, selected.resultId, session.token), 'karne.pdf');
        } catch (err: any) {
            setError(err?.message || 'PDF indirilemedi');
        } finally {
            setDownloading(false);
        }
    };

    if (linkError) {
        return (
            <KarneShell>
                <div className="mx-auto max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center">
                    <FileText className="mx-auto mb-2 text-slate-400" size={32} />
                    <h1 className="text-lg font-semibold text-slate-900">Karne bağlantısı açılamadı</h1>
                    <p className="mt-1 text-sm text-slate-600">{linkError}. Bağlantıyı okulunuzdan yeniden isteyin.</p>
                </div>
            </KarneShell>
        );
    }

    if (!school) {
        return <KarneShell><div className="py-20 text-center text-slate-500"><Loader2 className="mx-auto animate-spin" /></div></KarneShell>;
    }

    if (!session) {
        return (
            <KarneShell schoolName={school.name}>
                <div className="mx-auto max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h1 className="text-xl font-semibold text-slate-900">Deneme karnem</h1>
                    <p className="mt-1 text-sm text-slate-600">{school.name} öğrencisi olarak T.C. kimlik ve okul numaranla karnene ulaşabilirsin.</p>
                    <form onSubmit={login} className="mt-5 space-y-4">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">T.C. kimlik numarası</label>
                            <input
                                inputMode="numeric"
                                autoComplete="off"
                                maxLength={11}
                                value={tc}
                                onChange={(e) => setTc(e.target.value.replace(/\D/g, '').slice(0, 11))}
                                className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-base tracking-wider focus:border-transparent focus:ring-2 focus:ring-blue-500"
                                placeholder="11 haneli"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Okul numarası</label>
                            <input
                                inputMode="numeric"
                                autoComplete="off"
                                maxLength={10}
                                value={studentNumber}
                                onChange={(e) => setStudentNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-base focus:border-transparent focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <Turnstile onToken={setTurnstileToken} resetKey={turnstileKey} />
                        {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
                        <button
                            type="submit"
                            disabled={busy || tc.length !== 11 || !studentNumber || !turnstileToken}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                            {busy && <Loader2 size={16} className="animate-spin" />}
                            Karnemi göster
                        </button>
                    </form>
                    <p className="mt-4 text-xs text-slate-500">Bilgilerin yalnız karneni göstermek için kullanılır.</p>
                </div>
            </KarneShell>
        );
    }

    return (
        <KarneShell schoolName={school.name}>
            <div className="space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="text-xl font-semibold text-slate-900">{session.student.fullName || 'Öğrenci'}</h1>
                        <p className="text-sm text-slate-500">{[school.name, session.student.className].filter(Boolean).join(' · ')}</p>
                    </div>
                    <button onClick={() => logout()} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                        <LogOut size={15} /> Çıkış
                    </button>
                </div>
                {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

                {!selected ? (
                    <div className="space-y-2">
                        <h2 className="text-sm font-semibold text-slate-700">Denemelerin</h2>
                        {session.exams.map((exam) => (
                            <button
                                key={exam.resultId}
                                onClick={() => openExam(exam)}
                                className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-blue-300">
                                <div className="min-w-0">
                                    <div className="truncate font-medium text-slate-900">{exam.examName}</div>
                                    <div className="text-xs text-slate-500">{[formatDate(exam.examDate), exam.gradeLevel ? `${exam.gradeLevel}. sınıf` : null].filter(Boolean).join(' · ')}</div>
                                </div>
                                <div className="flex-shrink-0 text-right">
                                    <div className="text-lg font-bold text-slate-900">{formatNet(exam.totalNet)} net</div>
                                    <div className="text-xs text-slate-500">Okul sırası {rankText(exam.ranks?.school)}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            {session.exams.length > 1 ? (
                                <button onClick={() => { setSelected(null); setReportData(null); }} className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:underline">
                                    <ArrowLeft size={15} /> Denemelerim
                                </button>
                            ) : <span />}
                            <button
                                onClick={downloadPdf}
                                disabled={downloading || !reportData}
                                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                                {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Karneyi PDF indir
                            </button>
                        </div>
                        <RankCards summary={selected} />
                        <RankNote />
                        {loadingReport || !reportData ? (
                            <div className="py-16 text-center text-slate-500"><Loader2 className="mx-auto animate-spin" /></div>
                        ) : (
                            <WebStudentReportCard
                                data={reportData}
                                examName={selected.examName || ''}
                                companyLogo={systemConfig?.companyLogo || undefined}
                                companyName={systemConfig?.companyName || undefined}
                                totalStudents={selected.ranks?.general?.participantCount || 0}
                                disableAi
                            />
                        )}
                    </div>
                )}

                <div className="rounded-xl border border-blue-100 bg-blue-50 p-5">
                    <div className="flex items-start gap-3">
                        <Sparkles className="mt-0.5 flex-shrink-0 text-blue-600" size={20} />
                        <div>
                            <h3 className="font-semibold text-slate-900">Eksik kazanımlarını {systemConfig?.companyName || 'YES'} ile kapat</h3>
                            <p className="mt-1 text-sm text-slate-600">
                                Karnendeki eksik konular için konu anlatım videoları, yapay zekâ öğretmen ve sana özel çalışma planı.
                            </p>
                            <Link to="/register" className="mt-3 inline-flex rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                                Ücretsiz dene
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </KarneShell>
    );
}
